import React, { createContext, useState, useEffect } from "react";
import { useParams, Outlet } from "react-router-dom";
import api from "../services/api";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import type { TenantInfo, TenantContextValue } from "../types/tenant";

// =============================================================================
// Context
// =============================================================================

export const TenantContext = createContext<TenantContextValue | null>(null);

// =============================================================================
// TenantProvider
// Wraps tenant-scoped routes. Reads :tenantSlug from URL params,
// resolves to a tenant record, and provides the context to children.
// =============================================================================

export const TenantProvider: React.FC = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const resolve = async (): Promise<void> => {
      if (!tenantSlug) {
        setError("No tenant specified");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await api.resolveTenant(tenantSlug);

        if (cancelled) return;

        if (result.success && result.tenant) {
          setTenant(result.tenant);
          api.setTenantId(result.tenant.id);
        } else {
          setError(result.error || "Tenant not found");
        }
      } catch (err: unknown) {
        if (cancelled) return;
        console.error("Failed to resolve tenant:", err);
        setError("Failed to load tenant");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    resolve();

    return () => {
      cancelled = true;
    };
  }, [tenantSlug]);

  // Clear tenant ID from API when unmounting
  useEffect(() => {
    return () => {
      api.setTenantId(null);
    };
  }, []);

  const contextValue: TenantContextValue = {
    tenant,
    tenantId: tenant?.id || null,
    loading,
    error,
  };

  if (loading) {
    return null;
  }

  if (error || !tenant) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <ErrorState
          message={error || "Workspace not found"}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <TenantContext.Provider value={contextValue}>
      <Outlet />
    </TenantContext.Provider>
  );
};

export default TenantProvider;
